import React, { useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

import { useView } from "../context/ViewContext";
import { getValidToken } from "../utils/auth";
import {
  canUseProfessionalFeatures,
  canUseRecruiterFeatures,
  isPlatformAdminPayload,
} from "../utils/roleCapabilities";
import { DEFAULT_DASHBOARD_PATH } from "../utils/workspaceAuthorization";
import "./RecruiterRouteGuard.css";

const RecruiterRouteGuard = ({ children }) => {
  const { currentView, setCurrentView } = useView();
  const navigate = useNavigate();
  const location = useLocation();

  const auth = getValidToken();
  const hasSession = Boolean(auth?.token);
  const role = auth?.payload?.role;
  const isRecruiterRole = canUseRecruiterFeatures(role);
  const targetView = isPlatformAdminPayload(auth?.payload) ? "admin" : "professional";
  const isAllowedView = currentView === targetView;
  const isAllowed = hasSession && isRecruiterRole;
  const shouldSyncView = isRecruiterRole && !isAllowedView;
  const nextPath = `${location.pathname}${location.search}${location.hash}`;

  useEffect(() => {
    if (!shouldSyncView) {
      return;
    }

    setCurrentView(targetView);
  }, [setCurrentView, shouldSyncView, targetView]);

  // A signed-out visitor keeps their destination, the same way every other
  // protected route treats one. This used to bounce to "/" on a two-second
  // timer, so a recruiter opening an emailed link lost the link itself and
  // landed on the marketing home page.
  useEffect(() => {
    if (isAllowed || hasSession) {
      return;
    }

    navigate(`/login?next=${encodeURIComponent(nextPath)}`, { replace: true });
  }, [hasSession, isAllowed, navigate, nextPath]);

  if (shouldSyncView) {
    return <div>Loading...</div>;
  }

  if (isAllowed) {
    return children ?? null;
  }

  // The redirect above is already on its way; rendering the notice here would
  // flash "no recruiter access" at someone whose only problem is being signed out.
  if (!hasSession) {
    return null;
  }

  return (
    <div className="recruiter-gate">
      <section
        className="recruiter-gate__panel"
        role="alert"
        aria-labelledby="recruiter-gate-title"
      >
        <span className="recruiter-gate__chip">Recruiters only</span>

        <h1 className="recruiter-gate__title" id="recruiter-gate-title">
          This page is for recruiters
        </h1>

        <p className="recruiter-gate__copy">
          You&rsquo;re signed in, but this account doesn&rsquo;t have recruiter
          access. If you followed a recruiter link, it was most likely meant for
          a different account &mdash; sign in with that one and you&rsquo;ll come
          straight back here.
        </p>

        <div className="recruiter-gate__actions">
          <Link
            className="recruiter-gate__btn recruiter-gate__btn--primary"
            to={`/login?next=${encodeURIComponent(nextPath)}`}
          >
            Sign in with another account
          </Link>
          {canUseProfessionalFeatures(role) ? (
            <Link className="recruiter-gate__btn" to={DEFAULT_DASHBOARD_PATH}>
              Go to dashboard
            </Link>
          ) : (
            <Link className="recruiter-gate__btn" to="/">
              Go to home
            </Link>
          )}
        </div>

        <p className="recruiter-gate__meta">
          Think this is a mistake? <Link to="/contact">Contact us</Link> and we&rsquo;ll
          sort out your access.
        </p>
      </section>
    </div>
  );
};

export default RecruiterRouteGuard;
